import { Observable, from, defer, merge } from "rxjs"
import { switchMap, catchError, filter, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    createLoadingEnded,
    createLoadingEndedError,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_PAGINATION,
    SET_TRANSACTIONS_REPORT_DEFAULT_FILTER,
    setTotalCount,
    setTransactionsReportDefaultFilter,
} from "../../../filters/actions"
import { waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { getHttpHeaders } from "../../../auth/selectors"
import { getTransactionsQueryApiParam, isTransactionsReportAgendaOpened } from "./selectors"
import {
    clearTransactionsData,
    fetchTransactionsReportFailure,
    fetchTransactionsReportSuccess,
    TRANSACTIONS_AGENDA_MOUNTED,
    transactionsAgendaMounted,
} from "./actions"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import { ORDERS_AGENDA_MOUNTED } from "../orders/actions"
import { prepareQueryApiParameters } from "../../../../utils/queryApi/queryApiRequest"
import { fetchTransactionsReportData } from "./request"
import { Action, StateObject } from "../../../redux/types"

export const transactionsReportAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isTransactionsReportAgendaOpened(deps.getState())),
        switchMap(() => [transactionsAgendaMounted()])
    )

export const transactionsAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TRANSACTIONS_AGENDA_MOUNTED, ORDERS_AGENDA_MOUNTED),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => [clearTransactionsData(), setTransactionsReportDefaultFilter()])
    )

export const fetchTransactionsReportRequestedLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(TRANSACTIONS_AGENDA_MOUNTED, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE, SET_PAGINATION),
            filter(
                (action: Action) =>
                    action.payload.identifier === "transactionsReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isTransactionsReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return areAnyLocationsSelected(deps.getState())
                ? [createLoadingStarted("transactionsReport")]
                : [clearTransactionsData()]
        })
    )

export const transactionsReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_TRANSACTIONS_REPORT_DEFAULT_FILTER, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE, SET_PAGINATION),
            filter(
                (action: Action) =>
                    action.payload.identifier === "transactionsReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isTransactionsReportAgendaOpened(deps.getState())),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return defer(() =>
                    fetchTransactionsReportData(
                        getHttpHeaders(deps.getState()),
                        prepareQueryApiParameters(getTransactionsQueryApiParam(deps.getState()))
                    )
                ).pipe(
                    switchMap(response => {
                        if (response && response.data) {
                            return [
                                createLoadingEnded("transactionsReport"),
                                setTotalCount("transactionsReport", response.totalCount),
                                fetchTransactionsReportSuccess(response.data),
                            ]
                        }

                        return [createLoadingEndedError("transactionsReport"), fetchTransactionsReportFailure()]
                    }),
                    catchError(error =>
                        from([createLoadingEndedError("transactionsReport"), fetchTransactionsReportFailure(error)])
                    )
                )
            } else {
                return [createLoadingEnded("transactionsReport")]
            }
        })
    )
export default [
    transactionsReportFilterChanged,
    fetchTransactionsReportRequestedLoading,
    transactionsAgendaMountedEpics,
    transactionsReportAccountSwitch,
]
